import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { API_URL } from "../../config";
import Notification from "../../components/Notification";
import { allowedToView } from "../../utils/PermissionUtils";
import { formatEventDate } from "../../utils/TextUtils";

const STATUSES = ["Requested", "Approved", "Rejected", "Cancelled"];

// How many requests a page can show. The choice is remembered on this device.
const PAGE_SIZES = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_KEY = "requests_to_serve_page_size";

const savedPageSize = () => {
    try {
        const saved = Number(localStorage.getItem(PAGE_SIZE_KEY));
        return PAGE_SIZES.includes(saved) ? saved : DEFAULT_PAGE_SIZE;
    } catch (error) {
        return DEFAULT_PAGE_SIZE;
    }
};

const STATUS_BADGE = {
    requested: "is-pending",
    approved: "is-ok",
    rejected: "is-bad",
    cancelled: "is-info",
};

// "approved" or "Approved" -> "Approved"
const statusName = (status) => {
    const name = String(status || "Requested").toLowerCase();
    return name.charAt(0).toUpperCase() + name.slice(1);
};

// The requests to serve in missions. Admins and super admins see everyone's;
// any other member sees their own. A request is made, approved or rejected on
// the mission's own page: this page is the list across all missions.
function RequestsToServe() {
    const userRole = localStorage.getItem("userRole");
    const isAdmin = allowedToView(userRole, ["admin", "superadmin"]);

    const [list, setList] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(savedPageSize);
    const [status, setStatus] = useState("");
    const [scope, setScope] = useState("all");
    const [searchText, setSearchText] = useState("");
    const [search, setSearch] = useState("");
    const navigate = useNavigate();
    const location = useLocation();

    // The search runs a moment after typing stops.
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(searchText.trim());
            setPage(1);
        }, 350);
        return () => clearTimeout(timer);
    }, [searchText]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const query = new URLSearchParams({ page: String(page), per_page: String(perPage) });
            if (status) query.set("status", status);
            if (search) query.set("search", search);
            if (isAdmin && scope === "mine") query.set("scope", "mine");

            const response = await fetch(`${API_URL}/attendance-requests?${query}`, {
                headers: {
                    Accept: "application/json",
                    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                },
            });

            if (response.status === 401) {
                navigate("/logout", { state: { from: location } });
                return;
            }
            if (!response.ok) throw new Error("Failed to fetch the requests");

            setList(await response.json());
            setError("");
        } catch (problem) {
            setError("The requests to serve could not be loaded. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [page, perPage, status, search, scope, isAdmin, navigate, location]);

    useEffect(() => {
        document.title = "Requests to Serve - CFCM Portal";
        load();
    }, [load]);

    // What the API says wins over the role kept in the browser.
    const seesAll = list ? list.can_see_all && scope !== "mine" : isAdmin && scope !== "mine";
    const rows = list?.data || [];
    const filtered = Boolean(status || search);

    const choose = (setter) => (value) => {
        setter(value);
        setPage(1);
    };

    const choosePageSize = (value) => {
        const size = Number(value);
        setPerPage(size);
        setPage(1);
        try {
            localStorage.setItem(PAGE_SIZE_KEY, String(size));
        } catch (error) {
            // Private browsing: the choice lasts until the page is closed.
        }
    };

    // "Showing 21 to 40 of 57"
    const firstRow = list && list.total > 0 ? (list.current_page - 1) * list.per_page + 1 : 0;
    const lastRow = list ? Math.min(list.current_page * list.per_page, list.total) : 0;

    return (
        <main>
            <nav className="level">
                <div className="level-left">
                    <div className="level-item">
                        <div>
                            <p className="title is-4">{isAdmin ? "Requests to Serve" : "My Requests to Serve"}</p>
                            <p className="cf-muted">
                                {isAdmin
                                    ? "Members who have asked to serve in a mission."
                                    : "The missions you have asked to serve in."}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="level-right">
                    <p className="level-item">
                        <Link to="/dashboard/missions" className="button primary-outline-button">See missions</Link>
                    </p>
                </div>
            </nav>

            {error && <Notification type="danger" message={error} onClose={() => setError("")} />}

            {/* A short guide: how a request to serve is made and what happens to it */}
            <div className="cf-callout">
                <h4>How a request to serve is made</h4>
                <ol>
                    <li>Open <Link to="/dashboard/missions">Missions</Link> and choose a mission that is still to come.</li>
                    <li>On the mission's page, under Missioners, choose <strong>Request to Serve</strong> and confirm.</li>
                    <li>The admins are notified, and the request appears in this list as <strong>Requested</strong>.</li>
                    
                </ol>
            </div>

            {isAdmin && (
                <div className="cf-filter-tabs" role="group" aria-label="Whose requests to show">
                    <button type="button" className={scope === "all" ? "is-active" : ""} aria-pressed={scope === "all"} onClick={() => choose(setScope)("all")}>
                        All members
                    </button>
                    <button type="button" className={scope === "mine" ? "is-active" : ""} aria-pressed={scope === "mine"} onClick={() => choose(setScope)("mine")}>
                        My requests
                    </button>
                </div>
            )}

            <div className="columns is-variable is-2">
                <div className="column">
                    <div className="field">
                        <label className="label" htmlFor="rts-search">Search</label>
                        <div className="control">
                            <input
                                id="rts-search"
                                className="input"
                                type="search"
                                placeholder={seesAll ? "Member, school or mission" : "School or mission"}
                                value={searchText}
                                onChange={(event) => setSearchText(event.target.value)}
                            />
                        </div>
                    </div>
                </div>
                <div className="column is-narrow">
                    <div className="field">
                        <label className="label" htmlFor="rts-status">Status</label>
                        <div className="control">
                            <div className="select">
                                <select id="rts-status" value={status} onChange={(event) => choose(setStatus)(event.target.value)}>
                                    <option value="">All statuses</option>
                                    {STATUSES.map((name) => <option key={name} value={name}>{name}</option>)}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {!list && loading && <p>Loading the requests...</p>}

            {list && (
                <>
                    <p className="cf-muted mb-3" aria-live="polite">
                        {list.total === 1 ? "1 request" : `${list.total} requests`}
                    </p>

                    <div className="table-container" aria-busy={loading}>
                        <table className="table is-striped is-fullwidth">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    {seesAll && <th>Member</th>}
                                    {seesAll && <th>Phone</th>}
                                    <th>Mission</th>
                                    <th>Mission date</th>
                                    <th>Status</th>
                                    <th>Requested on</th>
                                    <th><span className="is-sr-only">Mission page</span></th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length > 0 ? rows.map((row, index) => (
                                    <tr key={row.id}>
                                        <td>{(list.current_page - 1) * list.per_page + index + 1}</td>
                                        {seesAll && (
                                            <td>
                                                <Link to={`/dashboard/members/${row.requested_by_id}/details`}>
                                                    {row.first_name} {row.last_name}
                                                </Link>
                                                <br />
                                                <span className="cf-muted is-size-7">{row.email}</span>
                                            </td>
                                        )}
                                        {seesAll && <td>{row.phone_number || "-"}</td>}
                                        <td>
                                            <Link to={`/dashboard/missions/${row.mission_id}/details`}>
                                                {row.school_name || row.mission_name}
                                            </Link>
                                            {row.school_name && row.mission_name && (
                                                <>
                                                    <br />
                                                    <span className="cf-muted is-size-7">{row.mission_name}</span>
                                                </>
                                            )}
                                        </td>
                                        <td>{formatEventDate(row.mission_date) || "-"}</td>
                                        <td>
                                            <span className={`cf-badge ${STATUS_BADGE[String(row.status).toLowerCase()] || "is-info"}`}>
                                                {statusName(row.status)}
                                            </span>
                                        </td>
                                        <td>{formatEventDate(row.created_at)}</td>
                                        <td className="has-text-right">
                                            <Link className="cf-row-link" to={`/dashboard/missions/${row.mission_id}/details`}>
                                                View mission
                                            </Link>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan={seesAll ? 8 : 6} className="has-text-centered">
                                            {filtered
                                                ? "No requests match."
                                                : seesAll
                                                    ? "No one has requested to serve yet."
                                                    : "You have not requested to serve in a mission yet."}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="cf-pager is-split">
                        <div className="cf-pager-size">
                            <label htmlFor="rts-page-size">Rows per page</label>
                            <div className="select is-small">
                                <select id="rts-page-size" value={perPage} onChange={(event) => choosePageSize(event.target.value)}>
                                    {PAGE_SIZES.map((size) => <option key={size} value={size}>{size}</option>)}
                                </select>
                            </div>
                        </div>

                        <span>
                            {list.total > 0 ? `Showing ${firstRow} to ${lastRow} of ${list.total}` : "Nothing to show"}
                        </span>

                        <div className="cf-pager-nav">
                            <button type="button" className="button is-small" disabled={list.current_page <= 1} onClick={() => setPage(1)} aria-label="First page">
                                First
                            </button>
                            <button type="button" className="button is-small" disabled={list.current_page <= 1} onClick={() => setPage(list.current_page - 1)}>
                                Previous
                            </button>
                            <span>Page {list.current_page} of {list.last_page}</span>
                            <button type="button" className="button is-small" disabled={list.current_page >= list.last_page} onClick={() => setPage(list.current_page + 1)}>
                                Next
                            </button>
                            <button type="button" className="button is-small" disabled={list.current_page >= list.last_page} onClick={() => setPage(list.last_page)} aria-label="Last page">
                                Last
                            </button>
                        </div>
                    </div>
                </>
            )}
        </main>
    );
}

export default RequestsToServe;
